import React, { useState, useEffect, useCallback } from 'react';
import {
    BarChart2, Users, Clock, Calendar, Filter,
    Download, RefreshCw, ChevronDown, AlertCircle, FileText
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../services/AuthContext';
import * as api from '../../services/api';
import './Admin.css';

export default function AdminSessionReport() {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const urlInstructorId = query.get('instructorId') || '';

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [instructors, setInstructors] = useState([]);
    const [filters, setFilters] = useState({
        startDate: firstOfMonth(),
        endDate: todayStr(),
        instructorId: isAdmin ? urlInstructorId : (user?._id || '')
    });
    const [activeTab, setActiveTab] = useState('sessions');

    // Update filters if URL changes (Admin only)
    useEffect(() => {
        if (isAdmin && urlInstructorId) {
            setFilters(prev => ({ ...prev, instructorId: urlInstructorId }));
        }
    }, [urlInstructorId, isAdmin]);

    const fetchReport = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.startDate) params.startDate = filters.startDate;
            if (filters.endDate) params.endDate = filters.endDate;
            if (isAdmin && filters.instructorId) params.instructorId = filters.instructorId;
            const res = await api.getAdminSessionReport(params);
            setReport(res.data);
        } catch {
            console.error('Failed to fetch report');
        } finally {
            setLoading(false);
        }
    }, [filters, isAdmin]);

    useEffect(() => {
        if (isAdmin) {
            (async () => {
                try {
                    const res = await api.getAllInstructors();
                    setInstructors(res.data || []);
                } catch { }
            })();
        }
    }, [isAdmin]);

    useEffect(() => { fetchReport(); }, [fetchReport]);

    const gt = report?.grandTotal || {};
    const tabs = [
        { key: 'sessions', label: 'All Sessions', icon: FileText },
        ...(isAdmin ? [{ key: 'instructors', label: 'By Instructor', icon: Users }] : []),
        { key: 'students', label: 'By Student', icon: BarChart2 }
    ];

    return (
        <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    <h1 style={{ fontSize: '1.5rem', fontWeight: 900, color: '#0f172a', letterSpacing: '-0.03em', margin: 0 }}>
                        Session Report
                    </h1>
                    <p style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '0.2rem' }}>
                        Instructor attendance analytics from session records
                    </p>
                </div>
                <button onClick={fetchReport} style={refreshBtnStyle}>
                    <RefreshCw size={15} /> Refresh
                </button>
            </div>

            {/* Filters */}
            <div style={{
                background: '#fff', borderRadius: '0.875rem', border: '1px solid #e2e8f0', padding: '1rem 1.25rem',
                marginBottom: '1.25rem', display: 'grid',
                gridTemplateColumns: isAdmin ? '1fr 1fr 1.5fr 100px' : '1fr 1fr 100px',
                gap: '0.75rem', alignItems: 'end'
            }}>
                <div style={fieldStyle}>
                    <label style={labelStyle}><Calendar size={12} /> Start Date</label>
                    <input type="date" style={inputStyle} value={filters.startDate}
                        onChange={e => setFilters({ ...filters, startDate: e.target.value })} />
                </div>
                <div style={fieldStyle}>
                    <label style={labelStyle}><Calendar size={12} /> End Date</label>
                    <input type="date" style={inputStyle} value={filters.endDate}
                        onChange={e => setFilters({ ...filters, endDate: e.target.value })} />
                </div>
                {isAdmin && (
                    <div style={fieldStyle}>
                        <label style={labelStyle}><Filter size={12} /> Instructor</label>
                        <select style={inputStyle} value={filters.instructorId}
                            onChange={e => setFilters({ ...filters, instructorId: e.target.value })}>
                            <option value="">All Instructors</option>
                            {instructors.map(i => <option key={i._id} value={i._id}>{i.fullName}</option>)}
                        </select>
                    </div>
                )}
                <button onClick={() => setFilters({ ...filters, startDate: firstOfMonth(), endDate: todayStr(), instructorId: isAdmin ? '' : (user?._id || '') })}
                    style={{ ...refreshBtnStyle, height: '38px', fontSize: '0.75rem' }}>
                    Reset
                </button>
            </div>

            {/* Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.875rem', marginBottom: '1.25rem' }}>
                <SummaryCard icon={FileText} label="Total Sessions" value={gt.totalSessions ?? '—'} color="#2563eb" bg="#dbeafe" loading={loading} />
                <SummaryCard icon={Clock} label="Total Hours" value={gt.totalHours?.toFixed(1) ?? '—'} color="#9333ea" bg="#f3e8ff" unit="h" loading={loading} />
                <SummaryCard icon={Users} label="Instructors" value={report?.byInstructor?.length ?? '—'} color="#16a34a" bg="#dcfce7" loading={loading} />
                <SummaryCard icon={BarChart2} label="Students" value={report?.byStudent?.length ?? '—'} color="#ea580c" bg="#ffedd5" loading={loading} />
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem', background: '#f1f5f9', borderRadius: '0.75rem', padding: '0.25rem' }}>
                {tabs.map(t => (
                    <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                        padding: '0.6rem', border: 'none', borderRadius: '0.625rem', cursor: 'pointer',
                        fontWeight: 700, fontSize: '0.8rem', transition: 'all .15s',
                        background: activeTab === t.key ? '#fff' : 'transparent',
                        color: activeTab === t.key ? '#0f172a' : '#64748b',
                        boxShadow: activeTab === t.key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none'
                    }}>
                        <t.icon size={14} /> {t.label}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <div style={{ background: '#fff', borderRadius: '1rem', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                {loading ? (
                    <div style={{ padding: '4rem', textAlign: 'center', color: '#94a3b8' }}>
                        <div style={{ width: 32, height: 32, border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin .7s linear infinite', margin: '0 auto 0.75rem' }} />
                        Loading report...
                    </div>
                ) : (
                    <>
                        {activeTab === 'sessions' && <SessionsTable sessions={report?.sessions || []} />}
                        {activeTab === 'instructors' && <AggTable data={report?.byInstructor || []} nameKey="fullName" idKey="instructorId" kind="Instructor" />}
                        {activeTab === 'students' && <AggTable data={report?.byStudent || []} nameKey="fullName" idKey="studentId" kind="Student" />}
                    </>
                )}
            </div>
        </div>
    );
}

// Sessions Table
function SessionsTable({ sessions }) {
    if (sessions.length === 0) return <EmptyState />;
    return (
        <div style={{ maxHeight: '520px', overflowY: 'auto' }}>
            <table className="admin-standard-table" style={{ width: '100%' }}>
                <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
                    <tr>
                        <th>Date</th>
                        <th>Instructor</th>
                        <th>Student</th>
                        <th>Duration</th>
                        <th>Notes</th>
                    </tr>
                </thead>
                <tbody>
                    {sessions.map((s, i) => (
                        <tr key={i}>
                            <td>{new Date(s.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                            <td style={{ fontWeight: 600 }}>{s.instructorId?.fullName || '—'}</td>
                            <td>{s.studentId?.fullName || '—'}</td>
                            <td>
                                <span style={{ background: '#eff6ff', color: '#2563eb', padding: '0.2rem 0.5rem', borderRadius: '0.25rem', fontWeight: 700, fontSize: '0.82rem' }}>
                                    {s.durationInHours}h
                                </span>
                            </td>
                            <td style={{ fontSize: '0.82rem', color: '#64748b', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {s.notes || '—'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// Aggregated Table (By Instructor or By Student)
function AggTable({ data, nameKey, idKey, kind }) {
    if (data.length === 0) return <EmptyState />;
    const total = data.reduce((a, r) => a + r.totalHours, 0);
    return (
        <div>
            <table className="admin-standard-table" style={{ width: '100%' }}>
                <thead>
                    <tr>
                        <th>{kind}</th>
                        <th>Sessions</th>
                        <th>Total Hours</th>
                        <th>Share</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((r, i) => {
                        const pct = total > 0 ? Math.round((r.totalHours / total) * 100) : 0;
                        return (
                            <tr key={i}>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                        <div style={{
                                            width: 32, height: 32, borderRadius: '0.5rem',
                                            background: kind === 'Instructor' ? '#dbeafe' : '#f3e8ff',
                                            color: kind === 'Instructor' ? '#2563eb' : '#9333ea',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontWeight: 900, fontSize: '0.8rem', flexShrink: 0
                                        }}>
                                            {r[nameKey]?.[0]?.toUpperCase() || '?'}
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{r[nameKey]}</div>
                                            {r.email && <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{r.email}</div>}
                                        </div>
                                    </div>
                                </td>
                                <td style={{ fontWeight: 700 }}>{r.totalSessions}</td>
                                <td>
                                    <span style={{ background: '#eff6ff', color: '#2563eb', padding: '0.2rem 0.5rem', borderRadius: '0.25rem', fontWeight: 800, fontSize: '0.85rem' }}>
                                        {r.totalHours.toFixed(1)}h
                                    </span>
                                </td>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <div style={{ flex: 1, height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                                            <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, #3b82f6, #1d4ed8)', borderRadius: 3, transition: 'width .4s' }} />
                                        </div>
                                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', minWidth: '2.5rem' }}>{pct}%</span>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr style={{ background: '#f8fafc' }}>
                        <td style={{ fontWeight: 900, color: '#0f172a' }}>Grand Total</td>
                        <td style={{ fontWeight: 800 }}>{data.reduce((a, r) => a + r.totalSessions, 0)}</td>
                        <td>
                            <span style={{ background: '#0f172a', color: '#fff', padding: '0.2rem 0.6rem', borderRadius: '0.25rem', fontWeight: 800, fontSize: '0.85rem' }}>
                                {total.toFixed(1)}h
                            </span>
                        </td>
                        <td style={{ fontWeight: 800, color: '#475569' }}>100%</td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
}

function EmptyState() {
    return (
        <div style={{ padding: '4rem', textAlign: 'center', color: '#94a3b8' }}>
            <FileText size={48} style={{ opacity: 0.2, margin: '0 auto 1rem' }} />
            <p style={{ fontWeight: 600, fontSize: '0.9rem' }}>No session data for the selected filters.</p>
        </div>
    );
}

function SummaryCard({ icon: Icon, label, value, color, bg, unit = '', loading }) {
    return (
        <div style={{ background: '#fff', borderRadius: '0.875rem', padding: '1rem 1.25rem', border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '0.875rem', boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }}>
            <div style={{ width: 42, height: 42, borderRadius: '0.75rem', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={20} color={color} />
            </div>
            <div>
                <div style={{ fontSize: '1.35rem', fontWeight: 900, color, lineHeight: 1 }}>{loading ? '...' : value}{unit}</div>
                <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600, marginTop: '0.15rem' }}>{label}</div>
            </div>
        </div>
    );
}

// Helpers
function todayStr() { return new Date().toISOString().split('T')[0]; }
function firstOfMonth() {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
}

const refreshBtnStyle = {
    display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem',
    borderRadius: '0.625rem', border: '1px solid #e2e8f0', background: '#fff',
    color: '#64748b', cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem'
};
const fieldStyle = { display: 'flex', flexDirection: 'column', gap: '0.2rem' };
const labelStyle = { fontSize: '0.68rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: '0.3rem' };
const inputStyle = {
    padding: '0.55rem 0.75rem', border: '1.5px solid #e2e8f0', borderRadius: '0.5rem',
    fontSize: '0.82rem', fontWeight: 600, color: '#0f172a', outline: 'none', background: '#f8fafc',
    boxSizing: 'border-box', width: '100%', fontFamily: 'inherit'
};
